import React, { useState, useContext, useEffect } from "react";
import { useHistory } from "react-router-dom";

import MenuItem from "@material-ui/core/MenuItem";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ConfirmationModal from "../ConfirmationModal";
import MessageReceiptsModal from "../MessageReceiptsModal";
import { Menu } from "@material-ui/core";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { ForwardMessageContext } from "../../context/ForwardingMessage/ForwardingMessageContext";
import { EditingMessageContext } from "../../context/EditingMessage/EditingMessageContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SettingsContext } from "../../context/Settings/SettingsContext";
import {
	isPerConnectionContact,
	isPerConnectionContactTickets,
} from "../../helpers/contactTicketMode";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { normalizeMediaUrl, extractMediaFilename } from "../../utils/mediaUrl";

const PENDING_PRIVATE_REPLY_KEY = "pendingPrivateReply";

const MessageOptionsMenu = ({ message, menuOpen, handleClose, anchorEl, anchorPosition, ticket }) => {
	const { setReplyingMessage } = useContext(ReplyMessageContext);
	const { startForwardSelection } = useContext(ForwardMessageContext);
	const editingContext = useContext(EditingMessageContext);
	const setEditingMessage = editingContext?.setEditingMessage;
	const { user } = useContext(AuthContext);
	const { getSetting } = useContext(SettingsContext);
	const history = useHistory();
	const [confirmationOpen, setConfirmationOpen] = useState(false);
	const [deleteForEveryone, setDeleteForEveryone] = useState(true);
	const [receiptsModalOpen, setReceiptsModalOpen] = useState(false);

	// Sem a mensagem o retorno é null e o Dialog nunca dispara onClose;
	// sem este reset o open=true ficaria órfão e reabriria no próximo clique.
	useEffect(() => {
		if (!message) {
			setReceiptsModalOpen(false);
		}
	}, [message]);

	if (!message) {
		return null;
	}

	const isAnchorElValid = Boolean(
		anchorEl &&
		typeof document !== "undefined" &&
		document.body.contains(anchorEl) &&
		anchorEl.getClientRects().length > 0
	);

	const shouldUseAnchorPosition = !isAnchorElValid && Boolean(anchorPosition);

	const handleSafeClose = (options = {}) => {
		if (typeof document !== "undefined") {
			const activeElement = document.activeElement;
			if (activeElement && typeof activeElement.blur === "function") {
				activeElement.blur();
			}
		}
		handleClose(options);
	};

	const resolveMediaUrl = (msg) => {
		if (!msg) return "";
		return String(msg.mediaUrl || "").trim();
	};

	const isStickerMessage = (msg) => {
		if (!msg) return false;

		if (msg.mediaType === "sticker" || msg.mediaType === "stickerMessage") {
			return true;
		}

		const body = String(msg.body || "").trim().toLowerCase();
		if (body === "sticker" || body.endsWith(": sticker") || body.endsWith(" sticker")) {
			return true;
		}

		const mediaUrl = resolveMediaUrl(msg).toLowerCase();
		if (mediaUrl.endsWith(".webp") || mediaUrl.includes(".webp?")) {
			return true;
		}

		const dataJsonRaw = msg.dataJson;
		const dataJsonText = typeof dataJsonRaw === "string" ? dataJsonRaw : JSON.stringify(dataJsonRaw || {});
		if (
			dataJsonText.includes("stickerMessage") ||
			dataJsonText.includes("image/webp") ||
			dataJsonText.includes("mimetype\":\"image/webp")
		) {
			return true;
		}

		return false;
	};

	const isImageMessage = (msg) => {
		if (!msg) return false;
		return ["image", "sticker", "stickerMessage", "imageMessage"].includes(msg.mediaType);
	};

	const isEditableTextMessage = (msg) => {
		if (!msg) return false;
		const mediaType = String(msg.mediaType || "").trim();
		if (!mediaType) return true;

		const editableTextTypes = ["chat", "conversation", "extendedTextMessage"];
		if (editableTextTypes.includes(mediaType)) {
			return true;
		}

		const editableImageTypes = ["image", "imageMessage"];
		if (editableImageTypes.includes(mediaType)) {
			return String(msg.body || "").trim().length > 0;
		}

		return false;
	};

	const getSanitizedTextForCopy = (msg) => {
		const rawBody = String(msg?.body || "");
		if (!rawBody) return "";

		let sanitized = rawBody;
		let parsedDataJson = null;
		if (msg?.dataJson) {
			try {
				parsedDataJson = typeof msg.dataJson === "string" ? JSON.parse(msg.dataJson) : msg.dataJson;
			} catch (e) {
				parsedDataJson = null;
			}
		}

		const participantNumber = String(msg?.participant || "")
			.split("@")[0]
			.split(":")[0]
			.trim();

		const signatureCandidates = [
			msg?.sign,
			msg?.userName,
			msg?.contact?.name,
			msg?.sender?.name,
			parsedDataJson?.pushName,
			parsedDataJson?.verifiedBizName,
			parsedDataJson?.notify,
			participantNumber,
		]
			.filter(Boolean)
			.map((value) => String(value).trim())
			.filter(Boolean);

		for (const name of signatureCandidates) {
			const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
			const markdownPrefixInnerColonRegex = new RegExp(`^\\*${escapedName}:\\*\\s*(?:\\r?\\n+)?`, "i");
			const markdownPrefixRegex = new RegExp(`^\\*${escapedName}\\*:\\s*(?:\\r?\\n+)?`, "i");
			const plainPrefixRegex = new RegExp(`^${escapedName}:\\s*(?:\\r?\\n+)?`, "i");

			if (markdownPrefixInnerColonRegex.test(sanitized)) {
				sanitized = sanitized.replace(markdownPrefixInnerColonRegex, "");
			}

			if (markdownPrefixRegex.test(sanitized)) {
				sanitized = sanitized.replace(markdownPrefixRegex, "");
			}

			if (plainPrefixRegex.test(sanitized)) {
				sanitized = sanitized.replace(plainPrefixRegex, "");
			}
		}

		if (msg?.signedMessage || msg?.signMessage || msg?.sign) {
			sanitized = sanitized
				.replace(/^\*[^*\n]+:\*\s*(?:\r?\n+)?/, "")
				.replace(/^\*[^*\n]+\*:\s*(?:\r?\n+)?/, "")
				.replace(/^[^\n:]{1,80}:\s*(?:\r?\n+)?/, "");
		}

		if (!msg?.fromMe) {
			const markdownNamedInnerColonPrefix = /^\*([\p{L}\p{N} ._'’-]{1,80}):\*\s*(?:\r?\n+)?/u;
			const markdownNamedPrefix = /^\*([\p{L}\p{N} ._'’-]{1,80})\*:\s*(?:\r?\n+)?/u;
			const plainNamedPrefix = /^([\p{L}\p{N} ._'’-]{1,80}):\s*(?:\r?\n+)?/u;

			if (markdownNamedInnerColonPrefix.test(sanitized)) {
				sanitized = sanitized.replace(markdownNamedInnerColonPrefix, "");
			}

			if (markdownNamedPrefix.test(sanitized)) {
				sanitized = sanitized.replace(markdownNamedPrefix, "");
			}

			if (plainNamedPrefix.test(sanitized)) {
				sanitized = sanitized.replace(plainNamedPrefix, "");
			}
		}

		return sanitized.trim();
	};

	// Desabilitar opções se o ticket estiver em pending
	const isTicketPending = ticket?.status === "pending";
	const isGroup = ticket?.isGroup || false;
	const isCloudApiConnection = ticket?.whatsapp?.type === "cloudapi";

	const resolveMessageDate = (msg) => {
		const fallbackDate = new Date(msg?.createdAt);

		const dataJsonRaw = msg?.dataJson;
		let dataJson = null;
		if (dataJsonRaw) {
			try {
				dataJson = typeof dataJsonRaw === "string" ? JSON.parse(dataJsonRaw) : dataJsonRaw;
			} catch (_err) {
				dataJson = null;
			}
		}

		const candidates = [
			dataJson?.messageTimestamp,
			msg?.timestamp,
		];

		for (const raw of candidates) {
			if (raw === undefined || raw === null) continue;
			const numeric = Number(raw);
			if (!Number.isFinite(numeric) || numeric <= 0) continue;

			// Heurística: >= 1e12 já está em ms; caso contrário, assume segundos.
			const millis = numeric >= 1e12 ? numeric : numeric * 1000;
			const candidateDate = new Date(millis);
			if (!Number.isNaN(candidateDate.getTime())) {
				return candidateDate;
			}
		}

		if (!Number.isNaN(fallbackDate.getTime())) {
			return fallbackDate;
		}

		return new Date();
	};

	// Verificar se a mensagem pode ser deletada para todos (limite de ~2 dias do WhatsApp)
	const canDeleteForEveryone = () => {
		if (!message) return false;
		const now = new Date();
		const messageDate = resolveMessageDate(message);
		
		const messageAge = now.getTime() - messageDate.getTime();
		const twoDaysInMs = 2 * 24 * 60 * 60 * 1000; // 2 dias em milissegundos
		
		return messageAge <= twoDaysInMs;
	};

	const handleDeleteMessage = async (deleteForEveryone = true) => {
		if (isTicketPending) return;
		try {
			await api.delete(`/messages/${message.id}`, { 
				data: { deleteForEveryone } 
			});
		} catch (err) {
			toastError(err);
		}
	};

	const parseParticipantIdentifier = (raw) => {
		if (!raw) return null;
		const value = String(raw).split(":")[0];
		if (value.includes("@lid")) {
			const lid = value.replace("@lid", "");
			return lid ? { lid } : null;
		}
		if (value.includes("@s.whatsapp.net") || value.includes("@c.us")) {
			const number = value.split("@")[0].replace(/\D/g, "");
			return number && number.length <= 15 ? { number } : null;
		}
		const cleaned = value.replace(/\D/g, "");
		if (cleaned && cleaned.length <= 15) {
			return { number: cleaned };
		}
		return null;
	};

	const getPrivateReplyIdentifier = (msg) => {
		if (!msg) return null;
		const direct = parseParticipantIdentifier(msg.participant);
		if (direct) return direct;
		if (!msg.dataJson) return null;
		try {
			const data = JSON.parse(msg.dataJson);
			const candidates = [
				data.message?.extendedTextMessage?.contextInfo?.participant,
				data.message?.imageMessage?.contextInfo?.participant,
				data.message?.videoMessage?.contextInfo?.participant,
				data.message?.documentMessage?.contextInfo?.participant,
				data.message?.audioMessage?.contextInfo?.participant,
				data.message?.stickerMessage?.contextInfo?.participant,
				data.participant,
				data.author,
				data.key?.participant,
				data.key?.remoteJid
			];
			for (const candidate of candidates) {
				const parsed = parseParticipantIdentifier(candidate);
				if (parsed) return parsed;
			}
			return null;
		} catch (e) {
			return null;
		}
	};

	const getPrivateReplyName = (msg, fallbackNumber) => {
		if (!msg) return fallbackNumber;
		if (msg.dataJson) {
			try {
				const data = JSON.parse(msg.dataJson);
				if (data.pushName) return data.pushName;
				if (data.verifiedBizName) return data.verifiedBizName;
				if (data.notify) return data.notify;
			} catch (e) {
				return fallbackNumber;
			}
		}
		return fallbackNumber;
	};

	const findContactByNumber = async (rawNumber) => {
		const cleanedNumber = rawNumber.replace(/\D/g, "");
		let contact = null;
		try {
			const { data } = await api.get(`/contacts/bynumber/${cleanedNumber}`);
			if (data.contact) {
				contact = data.contact;
			}
		} catch (err) {
			if (err.response?.status !== 404) {
				throw err;
			}
		}

		if (!contact) {
			const normalizedNumber = cleanedNumber.replace(/^(\d{2})(\d{2})(9?)(\d{4})(\d{4})$/, "$1$2$4$5");
			if (normalizedNumber !== cleanedNumber) {
				try {
					const { data } = await api.get(`/contacts/bynumber/${normalizedNumber}`);
					if (data.contact) {
						contact = data.contact;
					}
				} catch (err) {
					if (err.response?.status !== 404) {
						throw err;
					}
				}
			}
		}

		return { contact, cleanedNumber };
	};

	const resolveLidToNumber = async (lidValue) => {
		if (!lidValue) return null;
		try {
			const encoded = encodeURIComponent(lidValue);
			const { data } = await api.get(`/lid/reverse/${encoded}`);
			if (data?.success && data?.number) {
				return String(data.number);
			}
		} catch (err) {
			return null;
		}
		return null;
	};

	const buildPrivateReplyMessage = (msg, senderName) => {
		return {
			id: msg?.id || null,
			body: msg?.body || "",
			fromMe: false,
			mediaType: msg?.mediaType,
			mediaUrl: msg?.mediaUrl,
			contact: {
				name: senderName || msg?.contact?.name || "",
			},
		};
	};

	const handleReplyPrivate = async () => {
		if (isTicketPending) return;
		const privateIdentifier = getPrivateReplyIdentifier(message);
		if (!privateIdentifier) {
			toastError({ message: i18n.t("messageOptionsMenu.replyPrivateError") });
			return;
		}
		const fallbackNumber = privateIdentifier.number || privateIdentifier.lid || "";
		const senderName = getPrivateReplyName(message, fallbackNumber);
		try {
			let targetContact = null;
			let normalizedNumber = null;

			if (privateIdentifier.number) {
				const { contact, cleanedNumber } = await findContactByNumber(privateIdentifier.number);
				targetContact = contact;
				normalizedNumber = cleanedNumber;
			}

			if (!targetContact && privateIdentifier.lid) {
				const lidValue = `${privateIdentifier.lid}@lid`;
				let contactByLid = null;
				try {
					const { data } = await api.get(`/contacts/bynumber/${encodeURIComponent(lidValue)}`);
					if (data.contact) {
						contactByLid = data.contact;
					}
				} catch (err) {
					if (err.response?.status !== 404) {
						throw err;
					}
				}

				if (contactByLid) {
					targetContact = contactByLid;
				}

				const lidResolvedNumber = await resolveLidToNumber(privateIdentifier.lid);
				if (lidResolvedNumber) {
					const { contact } = await findContactByNumber(lidResolvedNumber);
					if (contact) {
						targetContact = contact;
						if (!contact.lid) {
							try {
								await api.put(`/contacts/${contact.id}`, { lid: lidValue });
							} catch (err) {
								// Ignore update errors here
							}
						}
					} else {
						const { data: newContact } = await api.post("/contacts", {
							name: senderName || "Contato sem nome",
							number: lidResolvedNumber,
							lid: lidValue
						});
						targetContact = newContact;
					}
				}

				if (!targetContact) {
					const { data: newContact } = await api.post("/contacts", {
						name: senderName || "Contato sem nome",
						lid: lidValue
					});
					targetContact = newContact;
				}
			}

			if (!targetContact && normalizedNumber) {
				const { data: newContact } = await api.post("/contacts", {
					name: senderName || "Contato sem nome",
					number: normalizedNumber
				});
				targetContact = newContact;
			}

			if (!targetContact) {
				toastError({ message: i18n.t("messageOptionsMenu.replyPrivateError") });
				return;
			}

			let ticketToUse = null;
			try {
				const { data: openTickets } = await api.get("/tickets", {
					params: {
						contactId: targetContact.id,
						status: "open",
						pageNumber: 1
					}
				});
				// Um por conexão: só o chamado da conexão deste ticket (é por ela que a
				// resposta sai); o de outra conexão é conversa de outro número.
				const candidates = isPerConnectionContactTickets(getSetting) && isPerConnectionContact(targetContact)
					? (openTickets.tickets || []).filter(
						(t) => Number(t.whatsappId) === Number(ticket?.whatsappId)
					)
					: openTickets.tickets || [];
				if (candidates.length > 0) {
					ticketToUse = candidates[0];
				}
			} catch (err) {
				// Se falhar, segue criando novo ticket abaixo
			}

			if (!ticketToUse) {
				const { data: newTicket } = await api.post("/tickets", {
					contactId: targetContact.id,
					queueId: ticket?.queueId || null,
					userId: user?.id,
					status: "open",
					whatsappId: ticket?.whatsappId || null,
				});
				ticketToUse = newTicket;
			}

			if (ticketToUse) {
				localStorage.setItem(
					PENDING_PRIVATE_REPLY_KEY,
					JSON.stringify({
						ticketId: ticketToUse.id,
						ticketUuid: ticketToUse.uuid,
						message: buildPrivateReplyMessage(message, senderName),
					})
				);
				history.push(`/tickets/${ticketToUse.uuid}`);
			}
		} catch (err) {
			if (err.response?.data?.error === "ERR_OTHER_OPEN_TICKET") {
				let ticketData = null;
				try {
					const details = err.response.data.details;
					ticketData = typeof details === "string" ? JSON.parse(details) : details;
				} catch (parseErr) {
					try {
						ticketData = JSON.parse(err.response.data.error);
					} catch (parseErrFallback) {
						ticketData = null;
					}
				}

				if (ticketData?.uuid) {
					localStorage.setItem(
						PENDING_PRIVATE_REPLY_KEY,
						JSON.stringify({
							ticketId: ticketData.id,
							ticketUuid: ticketData.uuid,
							message: buildPrivateReplyMessage(message, senderName),
						})
					);
					history.push(`/tickets/${ticketData.uuid}`);
					return;
				}
			}
			toastError(err);
		} finally {
			handleSafeClose();
		}
	};

	const hanldeReplyMessage = () => {
		if (isTicketPending) return;
		setReplyingMessage(message);
		handleSafeClose();
	};

    const hanldeForwardMessage = () => {
		if (isTicketPending) return;
		startForwardSelection(message);
		handleSafeClose();
	};

	const handleEditMessage = () => {
		if (isTicketPending) return;
		if (isCloudApiConnection) return;
		console.log("🔧 handleEditMessage chamado", { message, setEditingMessage });
		
		if (!setEditingMessage) {
			console.error("❌ setEditingMessage não está disponível!");
			toastError({ message: i18n.t("messageOptionsMenu.editErrors.cannotEdit") });
			handleSafeClose();
			return;
		}

		// Debug: verificar o formato da data
		console.log("📅 Dados da mensagem para edição:", {
			createdAt: message.createdAt,
			createdAtType: typeof message.createdAt,
			timestamp: message.timestamp,
			timestampType: typeof message.timestamp,
			now: new Date(),
			parsedCreatedAt: new Date(message.createdAt),
			parsedTimestamp: message.timestamp ? new Date(message.timestamp) : null
		});

		// Verifica se a mensagem não é muito antiga (15 minutos)
		const now = new Date();
		const messageDate = resolveMessageDate(message);
		
		const messageAge = now.getTime() - messageDate.getTime();
		const fifteenMinutesInMs = 15 * 60 * 1000;

		console.log("⏱️ Idade da mensagem:", {
			messageDate,
			messageAge,
			messageAgeMinutes: messageAge / 1000 / 60,
			fifteenMinutesInMs,
			canEdit: messageAge <= fifteenMinutesInMs
		});

		if (messageAge > fifteenMinutesInMs) {
			toastError({ message: i18n.t("messageOptionsMenu.editErrors.tooOld") });
			handleSafeClose();
			return;
		}

		// Verifica se a mensagem não foi deletada
		if (message.isDeleted) {
			toastError({ message: i18n.t("messageOptionsMenu.editErrors.deleted") });
			handleSafeClose();
			return;
		}

		// Verifica se é mensagem de texto (inclui tipos textuais do Baileys)
		if (!isEditableTextMessage(message)) {
			toastError({ message: i18n.t("messageOptionsMenu.editErrors.media") });
			handleSafeClose();
			return;
		}

		console.log("✅ Definindo mensagem para edição:", message);
		setEditingMessage(message);
		handleSafeClose();
	};

	const handleOpenConfirmationModal = (forEveryone = true) => {
		if (isTicketPending) return;
		setDeleteForEveryone(forEveryone);
		setConfirmationOpen(true);
		handleSafeClose({ preserveSelectedMessage: true });
	};

	const handleOpenReceiptsModal = () => {
		setReceiptsModalOpen(true);
		handleSafeClose({ preserveSelectedMessage: true });
	};

	const handleAddToMyStickers = async () => {
		if (isTicketPending) return;

		const mediaUrl = resolveMediaUrl(message);
		if (!mediaUrl) {
			toastError({ message: i18n.t("messageOptionsMenu.stickerNoMedia") });
			handleSafeClose();
			return;
		}

		try {
			await api.post("/messages/stickers/favorites", { mediaUrl });
			toast.success(i18n.t("messageOptionsMenu.stickerAdded"));
		} catch (err) {
			toastError(err);
		}

		handleSafeClose();
	};

	const handleCopyMessage = async () => {
		if (!navigator?.clipboard) {
			toastError({ message: i18n.t("messageOptionsMenu.copyError") });
			handleSafeClose();
			return;
		}

		const convertImageBlobToPng = async (blob) => {
			if (!blob) return null;

			try {
				const bitmap = await createImageBitmap(blob);
				const canvas = document.createElement("canvas");
				canvas.width = bitmap.width;
				canvas.height = bitmap.height;
				const ctx = canvas.getContext("2d");
				if (!ctx) return null;
				ctx.drawImage(bitmap, 0, 0);
				if (typeof bitmap.close === "function") {
					bitmap.close();
				}

				return await new Promise((resolve) => {
					canvas.toBlob((pngBlob) => resolve(pngBlob || null), "image/png");
				});
			} catch (_err) {
				return null;
			}
		};

		try {
			if (isImageMessage(message) && resolveMediaUrl(message)) {
				if (typeof window.ClipboardItem === "undefined" || !navigator.clipboard.write) {
					throw new Error("CLIPBOARD_IMAGE_UNSUPPORTED");
				}

				const mediaUrl = resolveMediaUrl(message);
				const response = await fetch(mediaUrl, { credentials: "include" });

				if (!response.ok) {
					throw new Error("FAILED_TO_FETCH_IMAGE");
				}

				const imageBlob = await response.blob();
				if (!imageBlob || !String(imageBlob.type || "").startsWith("image/")) {
					throw new Error("INVALID_IMAGE_BLOB");
				}

				try {
					const clipboardItem = new window.ClipboardItem({
						[imageBlob.type]: imageBlob,
					});
					await navigator.clipboard.write([clipboardItem]);
				} catch (_binaryCopyError) {
					const pngBlob = await convertImageBlobToPng(imageBlob);
					if (!pngBlob) {
						throw _binaryCopyError;
					}

					const pngClipboardItem = new window.ClipboardItem({
						"image/png": pngBlob,
					});
					await navigator.clipboard.write([pngClipboardItem]);
				}

				toast.success(i18n.t("messageOptionsMenu.copySuccess"));
			} else {
				const textToCopy = getSanitizedTextForCopy(message);
				if (!textToCopy) {
					toastError({ message: i18n.t("messageOptionsMenu.copyError") });
					handleSafeClose();
					return;
				}

				await navigator.clipboard.writeText(textToCopy);
				toast.success(i18n.t("messageOptionsMenu.copySuccess"));
			}
		} catch (err) {
			toastError({ message: i18n.t("messageOptionsMenu.copyError") });
		} finally {
			handleSafeClose();
		}
	};

	const handleDownloadImage = async () => {
		const mediaUrl = resolveMediaUrl(message);
		if (!mediaUrl) return;

		const normalizedUrl = normalizeMediaUrl(mediaUrl);
		const filename = message?.mediaName || extractMediaFilename(mediaUrl) || i18n.t("messageOptionsMenu.imageFilename");

		try {
			const response = await fetch(normalizedUrl, { credentials: "include" });
			if (!response.ok) throw new Error(`HTTP ${response.status}`);
			const blob = await response.blob();
			const objectUrl = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = objectUrl;
			link.download = filename;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(objectUrl);
		} catch (_err) {
			window.location.href = normalizedUrl;
		}

		handleSafeClose();
	};

	const handleToggleFavorite = async () => {
		if (isTicketPending) return;

		try {
			await api.post(`/messages/${message.id}/favorite`);
		} catch (err) {
			toastError(err);
		} finally {
			handleSafeClose();
		}
	};

	const handleTogglePending = async () => {
		if (isTicketPending) return;

		try {
			await api.post(`/messages/${message.id}/pending`);
		} catch (err) {
			toastError(err);
		} finally {
			handleSafeClose();
		}
	};

	return (
		<>
			<ConfirmationModal
				title={deleteForEveryone 
					? i18n.t("messageOptionsMenu.confirmationModal.titleDeleteForEveryone")
					: i18n.t("messageOptionsMenu.confirmationModal.titleDeleteForMe")
				}
				open={confirmationOpen}
				onClose={(isOpen) => {
					setConfirmationOpen(isOpen);
					if (!isOpen) {
						handleSafeClose();
					}
				}}
				onConfirm={() => handleDeleteMessage(deleteForEveryone)}
			>
				{deleteForEveryone 
					? i18n.t("messageOptionsMenu.confirmationModal.messageDeleteForEveryone")
					: i18n.t("messageOptionsMenu.confirmationModal.messageDeleteForMe")
				}
			</ConfirmationModal>
			
			<MessageReceiptsModal
				open={receiptsModalOpen}
				onClose={() => {
					setReceiptsModalOpen(false);
					handleSafeClose();
				}}
				message={message}
				isGroup={isGroup}
			/>
			
			<Menu
				anchorEl={shouldUseAnchorPosition ? undefined : anchorEl}
				anchorReference={shouldUseAnchorPosition ? "anchorPosition" : "anchorEl"}
				anchorPosition={shouldUseAnchorPosition ? anchorPosition : undefined}
				getContentAnchorEl={null}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				open={menuOpen}
				onClose={handleSafeClose}
				style={{ zIndex: 6000 }}
			>
				{message.fromMe && canDeleteForEveryone() && (
					<MenuItem onClick={() => handleOpenConfirmationModal(true)} disabled={isTicketPending}>
						{i18n.t("messageOptionsMenu.deleteForEveryone")}
					</MenuItem>
				)}
				{message.fromMe && (
					<MenuItem onClick={() => handleOpenConfirmationModal(false)} disabled={isTicketPending}>
						{i18n.t("messageOptionsMenu.deleteForMe")}
					</MenuItem>
				)}
				{message.fromMe && isEditableTextMessage(message) && !message.isDeleted && !isCloudApiConnection && (
					<MenuItem onClick={handleEditMessage} disabled={isTicketPending}>
						{i18n.t("messageOptionsMenu.edit")}
					</MenuItem>
				)}
				{message.fromMe && (
					<MenuItem onClick={handleOpenReceiptsModal}>
						{i18n.t("messageOptionsMenu.details")}
					</MenuItem>
				)}
				{isImageMessage(message) && (
					<MenuItem onClick={handleCopyMessage}>
						{i18n.t("messageOptionsMenu.copyImage")}
					</MenuItem>
				)}
				{isImageMessage(message) && !isStickerMessage(message) && resolveMediaUrl(message) && (
					<MenuItem onClick={handleDownloadImage}>
						{i18n.t("messageOptionsMenu.saveImage")}
					</MenuItem>
				)}
				{!isImageMessage(message) && (
					<MenuItem onClick={handleCopyMessage}>
						{i18n.t("messageOptionsMenu.copy")}
					</MenuItem>
				)}
				<MenuItem onClick={handleToggleFavorite} disabled={isTicketPending}>
					{message?.isFavorite
						? i18n.t("messageOptionsMenu.unfavorite")
						: i18n.t("messageOptionsMenu.favorite")}
				</MenuItem>
				<MenuItem onClick={handleTogglePending} disabled={isTicketPending}>
					{message?.isPending
						? i18n.t("messageOptionsMenu.unmarkPending")
						: i18n.t("messageOptionsMenu.markPending")}
				</MenuItem>
				<MenuItem onClick={hanldeForwardMessage} disabled={isTicketPending}>
					{i18n.t("messageOptionsMenu.forward")}
				</MenuItem>
				<MenuItem onClick={hanldeReplyMessage} disabled={isTicketPending}>
					{i18n.t("messageOptionsMenu.reply")}
				</MenuItem>
				{isGroup && !message?.fromMe && getPrivateReplyIdentifier(message) && (
					<MenuItem onClick={handleReplyPrivate} disabled={isTicketPending}>
						{i18n.t("messageOptionsMenu.replyPrivate")}
					</MenuItem>
				)}
				{!message?.fromMe && isStickerMessage(message) && (
					<MenuItem onClick={handleAddToMyStickers} disabled={isTicketPending}>
						{i18n.t("messageOptionsMenu.addToMyStickers")}
					</MenuItem>
				)}
			</Menu>
		</>
	);
};

export default MessageOptionsMenu;
